import validator from "validator";

/**
 * Validates and formats phone numbers for international use
 * Supports various international formats and E.164 standard
 */
export class PhoneValidation {
  /**
   * Validates if a phone number is valid for international use
   * @param phoneNumber - The phone number to validate
   * @returns boolean indicating if the phone number is valid
   */
  static isValid(phoneNumber: string): boolean {
    if (!phoneNumber || phoneNumber.trim() === '') {
      return false;
    }

    // Clean the phone number
    const cleaned = this.cleanPhoneNumber(phoneNumber);
    
    // Check if the cleaned number contains only valid characters (digits and +)
    if (!/^\+?[1-9]\d{1,14}$/.test(cleaned)) {
      return false;
    }
    
    // Check if it's a valid mobile phone number (international)
    // Using 'any' locale to support international numbers
    return validator.isMobilePhone(cleaned, 'any');
  }

  /**
   * Formats a phone number to E.164 format
   * @param phoneNumber - The phone number to format
   * @returns formatted phone number in E.164 format
   */
  static formatToE164(phoneNumber: string): string {
    if (!phoneNumber || phoneNumber.trim() === '') {
      return '';
    }

    // Remove all non-digit characters except +
    const cleaned = phoneNumber.replace(/[^\d+]/g, '');
    
    // Validate that the cleaned number is valid before formatting
    if (!/^\+?[1-9]\d{1,14}$/.test(cleaned)) {
      return '';
    }
    
    // If it already starts with +, use as is
    if (cleaned.startsWith('+')) {
      return cleaned;
    } else {
      // For numbers without country code, add + prefix
      // Note: This assumes US numbers if no country code is provided
      // In a production app, you might want to add a country code selector
      return `+${cleaned}`;
    }
  }

  /**
   * Cleans a phone number by removing formatting characters
   * @param phoneNumber - The phone number to clean
   * @returns cleaned phone number
   */
  static cleanPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return '';
    
    // Remove all non-digit characters except +
    return phoneNumber.replace(/[^\d+]/g, '');
  }

  /**
   * Gets validation error message for invalid phone numbers
   * @param phoneNumber - The phone number that failed validation
   * @returns error message string
   */
  static getErrorMessage(phoneNumber: string): string {
    if (!phoneNumber || phoneNumber.trim() === '') {
      return 'Phone number is required';
    }
    
    const cleaned = this.cleanPhoneNumber(phoneNumber);
    
    // Check for invalid characters
    if (!/^\+?[1-9]\d{1,14}$/.test(cleaned)) {
      return 'Phone number contains invalid characters. Only digits and + are allowed.';
    }
    
    if (cleaned.length < 10) {
      return 'Phone number is too short';
    }
    
    if (cleaned.length > 15) {
      return 'Phone number is too long';
    }
    
    return 'Invalid phone number format';
  }

  /**
   * Validates phone number and returns both validation result and formatted version
   * @param phoneNumber - The phone number to validate and format
   * @returns object with isValid boolean and formatted phone number
   */
  static validateAndFormat(phoneNumber: string): { isValid: boolean; formatted: string; errorMessage?: string } {
    const formatted = this.formatToE164(phoneNumber);
    const isValid = this.isValid(phoneNumber);
    
    return {
      isValid,
      formatted,
      errorMessage: isValid ? undefined : this.getErrorMessage(phoneNumber)
    };
  }
}
